import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../../services/api';
import { User } from '../../services/api';

// Query hooks
export const useUsersQuery = () => {
  return useQuery({
    queryKey: ['users'],
    queryFn: api.fetchUsers,
    staleTime: 2 * 60 * 1000, // 2 minutes - user data changes more frequently
  });
};

// Mutation hooks
export const useCreateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.createUser,
    onSuccess: () => {
      // Invalidate users list to fetch fresh data
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.deleteUser,
    onSuccess: (_, username: string) => {
      // Optimistically remove user from cache
      queryClient.setQueryData(['users'], (old: User[] = []) =>
        old.filter(u => u.username !== username)
      );
    },
    onError: () => {
      // Refresh users list on error
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useAddUserRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ username, role }: { username: string; role: string }) =>
      api.addUserRole(username, role),
    onSuccess: (_, { username, role }) => {
      // Optimistically update user roles
      queryClient.setQueryData(['users'], (old: User[] = []) => {
        return old.map(user => {
          if (user.username === username) {
            return { ...user, roles: [...user.roles, role] };
          }
          return user;
        });
      });
    },
    onError: () => {
      // Refresh users list on error
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useRemoveUserRoleMutation = () => {
  const queryClient = useQueryClient();

  const updateUserRoles = (users: User[], targetUsername: string, roleToRemove: string) => {
    return users.map(user => {
      if (user.username === targetUsername) {
        const updatedRoles = user.roles.filter(r => r !== roleToRemove);
        return { ...user, roles: updatedRoles };
      }
      return user;
    });
  };

  return useMutation({
    mutationFn: ({ username, role }: { username: string; role: string }) =>
      api.removeUserRole(username, role),
    onSuccess: (_, { username, role }) => {
      // Optimistically update user roles
      queryClient.setQueryData(['users'], (old: User[] = []) =>
        updateUserRoles(old, username, role)
      );
    },
    onError: () => {
      // Refresh users list on error
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};
